import { Dictionary } from "../Utils/Dictionary";
import { DeviceKey, IfKey, KeyNames, KeyPair } from "../Core/Keys";
import { MultiNode, MultiNodeType, MultiNodeKey } from "../Topology/MultiNode";
import { Device } from "../Core/Device";
import { DeviceNode } from "../Topology/DeviceNode";
import { TopoNode } from "../Topology/TopoNode";
import { List } from "../Utils/List";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";
import { TopoUtils } from "../Topology/TopoUtils";
import { DeviceGroupNode } from "./DeviceGroupNode";

//export class TopoNodeSetJson {
//    entries: TopoNodeEntryJson[];
//}

export class TopoNodeSet implements ObjectSerializer {
	private _deviceNodes: Dictionary<DeviceKey, DeviceNode>;
	private _deviceGroupNodes: Dictionary<string, DeviceGroupNode>;
	private _multiNodes: Dictionary<MultiNodeKey, MultiNode>;
	private _deviceNodeMap: Dictionary<Device, DeviceNode>;

	constructor() {
		this._deviceNodes = new Dictionary<DeviceKey, DeviceNode>();
		this._deviceGroupNodes = new Dictionary<string, DeviceGroupNode>();
		this._multiNodes = new Dictionary<MultiNodeKey, MultiNode>();
		this._deviceNodeMap = new Dictionary<Device, DeviceNode>();
	}

	public ToList(): List<TopoNode> {
		var nodes = new List<TopoNode>();
		nodes.UnionWith(this._deviceNodes.Values());
		nodes.UnionWith(this._deviceGroupNodes.Values());
		nodes.UnionWith(this._multiNodes.Values());
		return nodes;
	}

	public AddOrResolve_Multi(node: MultiNode): MultiNode {
		var existingNode: MultiNode = this.FindNode_MultiKey(node.Key);
		if (existingNode != null) {
			existingNode.Resolve(node);
			return existingNode;
		}
		else {
			this.AddNode(node);
			return node;
		}
	}

	public AddOrResolve_Device(node: DeviceNode): DeviceNode {
		var existingNode: DeviceNode = this.FindNode_DeviceKey(node.Key);
		if (existingNode != null) {
			existingNode.ResolveNode(node);

			if (existingNode.Device != null)
				this._deviceNodeMap.Add(existingNode.Device, existingNode);

			return existingNode;
		}
		else {
			this.AddNode(node);
			return node;
		}
	}

	public AddOrResolve_DeviceGroup(node: DeviceGroupNode): DeviceGroupNode {
		var existingNode: DeviceGroupNode = this.FindNode_DeviceGroupGuid(node.DeviceGroup.GUID);
		if (existingNode != null) {
			existingNode.ResolveNode(node);
			return existingNode;
		}
		else {
			this.AddNode(node);
			return node;
		}
	}

	public UnResolve(): void {
		this._multiNodes.ForEach((pair) => {
			pair.Value.UnResolve();
		});

		this._deviceNodes.ForEach((pair) => {
			pair.Value.UnResolve();
		});

		this._deviceGroupNodes.ForEach((pair) => {
			pair.Value.UnResolve();
		});
		this._deviceNodeMap.Clear();
	}

	private AddNode(node: TopoNode): void {
		if (node instanceof DeviceNode)
			this.AddDeviceNode(<DeviceNode>node);
		else if (node instanceof DeviceGroupNode)
			this.AddDeviceGroupNode(<DeviceGroupNode>node);
		else if (node instanceof MultiNode)
			this.AddMultiNode(<MultiNode>node);
	}

	public RemoveNode(node: TopoNode): void {
		if (node instanceof DeviceNode)
			this.RemoveDeviceNode(<DeviceNode>node);
		else if (node instanceof DeviceGroupNode)
			this.RemoveDeviceGroupNode(<DeviceGroupNode>node);
		else if (node instanceof MultiNode)
			this.RemoveMultiNode(<MultiNode>node);
	}

	public ContainsDeviceNode(device: Device): boolean {
		return this._deviceNodeMap.ContainsKey(device);
	}

	public ContainsDeviceNode_Key(key: DeviceKey): boolean {
		return this._deviceNodes.ContainsKey(key);
	}

	public FindNode(node: TopoNode): TopoNode {
		if (node instanceof DeviceNode)
			return this.FindNode_DeviceKey((<DeviceNode>node).Key);
		else if (node instanceof DeviceGroupNode)
			return this.FindNode_DeviceGroupGuid((<DeviceGroupNode>node).DeviceGroup.GUID);
		else if (node instanceof MultiNode)
			return this.FindNode_MultiKey((<MultiNode> node).Key);
		else
			return null;
	}

	public FindNode_Device(device: Device): DeviceNode {
		return this._deviceNodeMap.Item(device);
	}

	public FindNode_DeviceKey(key: DeviceKey): DeviceNode {
		return this._deviceNodes.Item(key);
	}

	public FindNode_DeviceGroupGuid(guid: string): DeviceGroupNode {
		return this._deviceGroupNodes.Item(guid);
	}

	public FindNode_MultiKey(key: MultiNodeKey): MultiNode {
		return this._multiNodes.Item(key);
	}

	public AddDeviceNode(node: DeviceNode): void {
		this._deviceNodes.Add(node.Key, node);

		if (node.Device != null)
			this._deviceNodeMap.Add(node.Device, node);
	}

	private RemoveDeviceNode(node: DeviceNode): void {
		this._deviceNodes.Remove(node.Key);
		if (node.Device != null)
			this._deviceNodeMap.Remove(node.Device);
	}

	public get DeviceNodes(): List<DeviceNode> {
		return this._deviceNodes.Values();
	}

	public get DeviceGroupNodes(): List<DeviceGroupNode> {
		return this._deviceGroupNodes.Values();
	}

	public AddDeviceGroupNode(node: DeviceGroupNode): void {
		this._deviceGroupNodes.Add(node.DeviceGroup.GUID, node);
	}

	public RemoveDeviceGroupNode(node: DeviceGroupNode): void {
		this._deviceGroupNodes.Remove(node.DeviceGroup.GUID);
	}

	public get MultiNodes(): List<MultiNode> {
		return this._multiNodes.Values();
	}

	public AddMultiNode(node: MultiNode): void {
		this._multiNodes.Add(node.Key, node);
	}

	public RemoveMultiNode(node: MultiNode): void {
		this._multiNodes.Remove(node.Key);
	}

	public Clear(): void {
		this._deviceNodes.Clear();
		this._deviceGroupNodes.Clear();
		this._multiNodes.Clear();
		this._deviceNodeMap.Clear();
	}

	public get Count(): number {
		return this._deviceNodes.Count + this._multiNodes.Count + this._deviceGroupNodes.Count;
    }

    //public static FromJson(json: TopoNodeSetJson): TopoNodeSet {
    //    var result = new TopoNodeSet();

    //    if (json.entries !== undefined) {
    //        json.entries.forEach((entry) => {
    //            result.AddNode(TopoNode.ReadNode(entry));
    //        }, this);
    //    }

    //    return result;
    //}

	public Write(obj: any, context: any): void {
		obj.topo_nodes = {};
		obj = obj.topo_nodes;

		obj.entries = [];
		ObjSerUtils.WriteObjSerCollection(obj.entries, this._deviceNodes.Values(), context);
		ObjSerUtils.WriteObjSerCollection(obj.entries, this._deviceGroupNodes.Values(), context);
		ObjSerUtils.WriteObjSerCollection(obj.entries, this._multiNodes.Values(), context); 
	}

	public Read(obj: any, context: any): void {
		if (!obj.topo_nodes)
			throw new Error();
		obj = obj.topo_nodes;

		if (obj.entries !== undefined) {
			obj.entries.forEach((value: any) => {
				let node: TopoNode = TopoUtils.ReadTopoNode(value, context);
				if (node)
					this.AddNode(node);
				else
					console.log("TopoNode could not be deserialized");
			}, this);
		}
	}
}
